import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "antd";
import dayjs from "dayjs";

import { GetNotificationsQuery } from "graphql/queries/get-notifications.generated";
import { UpdateNotificationMutationVariables } from "graphql/mutations/update-notification.generated";

interface FastLaneReadyNotificationProps {
	notification: GetNotificationsQuery["notifications"][number];
	updateNotification: ({ variables }: { variables: UpdateNotificationMutationVariables }) => void;
	// true on the /notifications page: the body wraps to several lines.
	// false in the header popover and the dashboard: the body stays on one line.
	isExpanded?: boolean;
}

/** Notification sent to AHJ users when an inspection is Fast Lane or a plan review is APPROVED.
 *  The whole row opens the inspection or plan review and marks the notification as viewed. */
const FastLaneReadyNotification = ({
	notification,
	updateNotification,
	isExpanded = false
}: FastLaneReadyNotificationProps) => {
	const isUnread = !notification.viewed;
	const bodyRef = useRef<HTMLParagraphElement>(null);
	const [isBodyTooltipOpen, setIsBodyTooltipOpen] = useState(false);

	// The body is cut with an ellipsis when it does not fit in one line.
	// Open the tooltip with the full text only in that case.
	const handleBodyTooltipOpenChange = (shouldOpen: boolean) => {
		const bodyElement = bodyRef.current;
		const isBodyTruncated = !!bodyElement && bodyElement.scrollWidth > bodyElement.clientWidth;
		setIsBodyTooltipOpen(shouldOpen && isBodyTruncated);
	};

	const markNotificationAsViewed = () => {
		updateNotification({ variables: { id: notification.id, _set: { viewed: true } } });
	};

	return (
		<Link
			className={`s-c-notification fast-lane-ready-notification${isExpanded ? " flrn-expanded" : ""}`}
			to={notification.deeplink || "/dashboard"}
			onClick={markNotificationAsViewed}
		>
			<div className="flrn-icon-circle">
				<img
					className="flrn-icon"
					src={`${process.env.PUBLIC_URL}/assets/images/icons/fast-lane-zap.svg`}
					width={25}
					height={25}
					alt=""
				/>
				{isUnread && <span className="flrn-unread-dot" />}
			</div>
			<div className="flrn-content">
				<div className="flrn-text">
					<p className={`flrn-title${isUnread ? " flrn-title-unread" : ""}`}>{notification.payload.title}</p>
					<Tooltip
						title={notification.body}
						open={isBodyTooltipOpen}
						onOpenChange={handleBodyTooltipOpenChange}
						mouseLeaveDelay={0}
					>
						<p ref={bodyRef} className="flrn-body">
							{notification.body}
						</p>
					</Tooltip>
					<p className="flrn-created-at">{dayjs(notification.created_at).format("ddd, MMM D [at] h:mm A")}</p>
				</div>
				<span className="flrn-view-label">View</span>
			</div>
		</Link>
	);
};

export default FastLaneReadyNotification;
